"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useCallback, useRef } from "react";

import { springSnappy } from "@/lib/animations";
import { cn } from "@/lib/utils";

const STOPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4] as const;

interface MultiplierSliderProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

function nearestStop(clientX: number, trackEl: HTMLDivElement) {
  const rect = trackEl.getBoundingClientRect();
  const pct = (clientX - rect.left) / rect.width;
  const max = STOPS.length - 1;
  const nearest = Math.round(pct * max);
  return STOPS[Math.max(0, Math.min(max, nearest))];
}

export function MultiplierSlider({ value, onChange, className }: MultiplierSliderProps) {
  const t = useTranslations("recipeDetail");
  const trackRef = useRef<HTMLDivElement>(null);
  const idx = STOPS.indexOf(value as (typeof STOPS)[number]);
  const activeIdx = idx === -1 ? STOPS.indexOf(1) : idx;
  const max = STOPS.length - 1;
  const fillPct = (activeIdx / max) * 100;
  const label = value === 1 ? "1×" : `${value}×`;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        if (activeIdx < max) onChange(STOPS[activeIdx + 1]);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        if (activeIdx > 0) onChange(STOPS[activeIdx - 1]);
      } else if (e.key === "Home") {
        e.preventDefault();
        onChange(STOPS[0]);
      } else if (e.key === "End") {
        e.preventDefault();
        onChange(STOPS[max]);
      }
    },
    [activeIdx, max, onChange]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const track = trackRef.current;
      if (!track) return;
      e.preventDefault();
      track.setPointerCapture(e.pointerId);
      onChange(nearestStop(e.clientX, track));

      const onMove = (ev: PointerEvent) => onChange(nearestStop(ev.clientX, track));
      const onUp = (ev: PointerEvent) => {
        track.releasePointerCapture(ev.pointerId);
        track.removeEventListener("pointermove", onMove);
        track.removeEventListener("pointerup", onUp);
      };
      track.addEventListener("pointermove", onMove);
      track.addEventListener("pointerup", onUp);
    },
    [onChange]
  );

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      <span className="text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {t("scaleRecipe")}
      </span>

      {/* Track + labels share the same inset so dots and labels align */}
      <div className="px-4">
        {/* Slider track */}
        <div
          ref={trackRef}
          role="slider"
          aria-label={t("scaleRecipe")}
          aria-valuemin={STOPS[0]}
          aria-valuemax={STOPS[max]}
          aria-valuenow={value}
          aria-valuetext={label}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          className="relative h-7 cursor-pointer touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {/* Background track */}
          <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-border" />
          {/* Active fill */}
          <motion.div
            className="absolute start-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-primary"
            animate={{ width: `${fillPct}%` }}
            transition={springSnappy}
          />
          {/* Tick marks */}
          {STOPS.map((s, i) => {
            const isActive = i <= activeIdx;
            const isCurrent = i === activeIdx;
            return (
              <motion.div
                key={s}
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ insetInlineStart: `${(i / max) * 100}%` }}
                animate={{ scale: isCurrent ? 1.3 : 1 }}
                transition={springSnappy}
              >
                {isCurrent ? (
                  <div className="size-2.5 rounded-full border-2 border-primary bg-card" />
                ) : (
                  <div
                    className={cn(
                      "size-1.5 rounded-full transition-colors",
                      isActive ? "bg-primary" : "bg-border"
                    )}
                  />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Stop labels — positioned at the same percentages as their dots */}
        <div className="relative h-4">
          {STOPS.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange(s)}
              className={cn(
                "absolute -translate-x-1/2 cursor-pointer text-[10.5px] text-muted-foreground transition-colors hover:text-foreground",
                s === value && "font-semibold text-foreground"
              )}
              style={{ insetInlineStart: `${(i / max) * 100}%` }}
            >
              {s === 1 ? "1×" : `${s}×`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export { STOPS as MULTIPLIER_STOPS };
